import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Sparkles, Tag, Bell, Loader2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WaitlistForm = () => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(() => localStorage.getItem("nb_waitlist") === "1");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) return toast.error("Please enter a valid email");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email });
      toast.success(data.message);
      localStorage.setItem("nb_waitlist", "1");
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join the list. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done) return <p data-testid="waitlist-done" className="text-[11px] text-goldlight font-semibold tracking-wide">✓ You're on the launch list</p>;

  return (
    <form data-testid="waitlist-form" onSubmit={submit} className="flex w-full sm:w-auto">
      <input
        data-testid="waitlist-email-input"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email me on launch day"
        className="flex-1 sm:w-56 bg-cream/10 border border-cream/25 rounded-l-full px-4 py-1.5 text-xs placeholder:text-cream/50 focus:outline-none focus:border-goldlight transition-colors"
      />
      <button data-testid="waitlist-submit-button" disabled={busy} className="inline-flex items-center gap-1.5 bg-gold hover:bg-goldlight text-white rounded-r-full px-3.5 text-[10px] font-bold uppercase tracking-[0.14em] transition-colors disabled:opacity-60">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />} Notify me
      </button>
    </form>
  );
};

export const AnnouncementBar = () => {
  const s = useSettings();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem("nb_announce_hidden") === "1");
  const showSale = s.sale_banner_enabled && s.sale_banner_text;
  if ((!s.coming_soon && !showSale) || hidden) return null;

  const dismiss = () => {
    sessionStorage.setItem("nb_announce_hidden", "1");
    setHidden(true);
  };

  return (
    <div data-testid="coming-soon-banner" className={`relative z-[55] text-cream ${showSale && !s.coming_soon ? "bg-gold" : "bg-forestdeep"}`}>
      <div className="container-nb flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-6 py-2.5 pr-10 text-center">
        {s.coming_soon && (
          <p data-testid="coming-soon-message" className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em]">
            <Sparkles size={14} className="text-goldlight shrink-0" /> {s.coming_soon_message}
          </p>
        )}
        {s.coming_soon && <WaitlistForm />}
        {showSale && (
          <p data-testid="sale-banner-message" className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.16em]">
            <Tag size={13} className={s.coming_soon ? "text-goldlight" : "text-white"} /> {s.sale_banner_text}
          </p>
        )}
        <button data-testid="coming-soon-dismiss" onClick={dismiss} aria-label="Dismiss announcement" className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-cream/60 hover:text-cream hover:bg-cream/10 transition-colors">
          <X size={14} />
        </button>
      </div>
    </div>
  );
};
